import { useState, useEffect } from 'react';
import axios from 'axios';
import { DataGrid } from '@mui/x-data-grid';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Link } from 'react-router-dom';
import Actions from './Actions';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const ruleLabels = { bogo_free: "Buy X Get Y Free", bogo_percent: "Buy X Get Y % Off", bulk_percent: "Bulk % Off" };

const OfferTable = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [offers, setOffers] = useState([]);

    const { user } = useSelector((state) => state.user);
    const isSuperAdmin = user.role === "admin";
    const perms = user.adminRole?.permissions?.offers || {};
    const canCreate = isSuperAdmin || perms.create;
    const canEdit = isSuperAdmin || perms.edit;
    const canDelete = isSuperAdmin || perms.delete;

    const fetchOffers = async () => {
        try {
            const { data } = await axios.get('/api/v1/admin/offers');
            setOffers(data.offers);
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to load offers", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOffers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const deleteHandler = async (id) => {
        try {
            await axios.delete(`/api/v1/admin/offer/${id}`);
            enqueueSnackbar("Offer deleted", { variant: "success" });
            setOffers((prev) => prev.filter((o) => o._id !== id));
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to delete offer", { variant: "error" });
        }
    };

    const columns = [
        {
            field: "name",
            headerName: "Name",
            minWidth: 200,
            flex: 1,
        },
        {
            field: "ruleType",
            headerName: "Rule",
            minWidth: 170,
            flex: 0.5,
        },
        {
            field: "products",
            headerName: "Products",
            minWidth: 200,
            flex: 0.8,
        },
        {
            field: "active",
            headerName: "Active",
            minWidth: 90,
            flex: 0.3,
            renderCell: (params) => (
                params.row.active
                    ? <span className="text-green-700 bg-green-100 px-2 py-0.5 rounded-full text-xs font-medium">Active</span>
                    : <span className="text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full text-xs font-medium">Hidden</span>
            ),
        },
        {
            field: "actions",
            headerName: "Actions",
            minWidth: 100,
            flex: 0.3,
            sortable: false,
            renderCell: (params) => (
                <Actions editRoute={"offer"} deleteHandler={deleteHandler} id={params.row.id} name={params.row.name} canEdit={canEdit} canDelete={canDelete} />
            ),
        },
    ];

    const rows = offers.map((o) => ({
        id: o._id,
        name: o.name,
        ruleType: ruleLabels[o.ruleType] || o.ruleType,
        products: (o.products || []).map((p) => p.name).join(", ") || "—",
        active: o.active,
    }));

    return (
        <>
            <MetaData title="Admin: Offers | 1313 Mart" />
            {loading && <BackdropLoader />}

            <div className="flex justify-between items-center mb-4">
                <div>
                    <Link to="/admin/site-content" className="text-sm text-primary-blue hover:underline">&larr; Back to Site Content</Link>
                    <h1 className="text-lg font-medium uppercase mt-1">Offers</h1>
                </div>
                {canCreate && (
                    <Link to="/admin/offer/new" className="py-2 px-4 rounded shadow font-medium text-white bg-primary-blue hover:shadow-lg">New Offer</Link>
                )}
            </div>

            <div className="bg-white rounded-xl shadow-lg w-full" style={{ height: 470 }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    pageSize={10}
                    disableSelectIconOnClick
                    sx={{ boxShadow: 0, border: 0 }}
                />
            </div>
        </>
    );
};

export default OfferTable;